import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Star, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewFormPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ pharmacy_name: "", city: "", rating: 5, text: "" });

  useEffect(() => {
    axios.get(`${API}/reviews/token/${token}`)
      .then((r) => {
        setInfo(r.data);
        setForm((f) => ({ ...f, pharmacy_name: r.data.pharmacy_name || "" }));
        if (r.data.used) setSent(true);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Este enlace no es válido"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/reviews/token/${token}`, form);
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo enviar la reseña");
    } finally {
      setSaving(false);
    }
  };

  const valid = form.pharmacy_name.trim().length > 1 && form.city.trim().length > 1
    && form.text.trim().length >= 20;

  return (
    <div className="min-h-screen bg-slate-50 flex items-start sm:items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-2xl" data-testid="review-form-page">
        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-emerald-700">FarmaClass</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-2">
          Cuéntanos qué tal os va.
        </h1>

        {loading && (
          <div className="mt-8 flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
          </div>
        )}

        {!loading && error && !sent && (
          <div className="mt-8 rounded-2xl border border-rose-200 bg-rose-50 p-6 flex items-start gap-3"
               data-testid="review-form-error">
            <AlertTriangle className="h-6 w-6 text-rose-600 shrink-0" />
            <div>
              <div className="font-bold text-rose-900">{error}</div>
              <p className="text-sm text-rose-900/80 mt-1">
                Si el enlace ha caducado, pídenos otro y te lo enviamos al momento.
              </p>
            </div>
          </div>
        )}

        {!loading && sent && (
          <div className="mt-8 rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-6 flex items-start gap-3"
               data-testid="review-form-sent">
            <CheckCircle2 className="h-7 w-7 text-emerald-600 shrink-0" />
            <div>
              <div className="font-bold text-emerald-900 text-lg">¡Gracias!</div>
              <p className="text-sm text-emerald-900/80 mt-1">
                Ya tenemos tu reseña. La revisaremos y la publicaremos en la web de FarmaClass.
              </p>
            </div>
          </div>
        )}

        {!loading && !sent && !error && info && (
          <form onSubmit={submit} className="mt-7 rounded-2xl border border-slate-200 bg-white p-6 sm:p-7 space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Farmacia</span>
                <input
                  value={form.pharmacy_name}
                  onChange={(e) => setForm({ ...form, pharmacy_name: e.target.value })}
                  className="mt-1.5 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                  placeholder="Farmacia Gemma Baradad"
                  data-testid="review-input-pharmacy"
                />
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Localidad</span>
                <input
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="mt-1.5 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                  placeholder="Rubí"
                  data-testid="review-input-city"
                />
              </label>
            </div>

            <div>
              <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Valoración</span>
              <div className="flex items-center gap-1.5 mt-1.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setForm({ ...form, rating: n })}
                    className="p-1"
                    title={`${n} de 5`}
                    data-testid={`review-star-${n}`}
                  >
                    <Star className={`h-7 w-7 transition-colors ${n <= form.rating ? "text-amber-500 fill-amber-500" : "text-slate-300"}`} />
                  </button>
                ))}
                <span className="text-sm font-bold text-slate-600 ml-2">{form.rating}/5</span>
              </div>
            </div>

            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Tu reseña</span>
              <textarea
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                rows={6}
                className="mt-1.5 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                placeholder="¿Qué os ha aportado FarmaClass en el día a día? ¿Qué parte usáis más?"
                data-testid="review-input-text"
              />
              <span className="text-[11px] text-slate-500">
                {form.text.trim().length < 20
                  ? `Escribe al menos 20 caracteres (${form.text.trim().length})`
                  : `${form.text.trim().length} caracteres`}
              </span>
            </label>

            <button
              type="submit"
              disabled={!valid || saving}
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-sm font-bold px-5 py-3 rounded-lg"
              data-testid="review-submit"
            >
              {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Enviando…</> : "Enviar mi reseña"}
            </button>
            <p className="text-[11px] text-slate-500">
              Publicaremos tu nombre de farmacia y localidad junto a la reseña. Nada más.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
